/**
 * 路径 1.5 — §37 storage bridge
 * BrowserDocumentStore Singleton
 *
 * Why a separate dbName: prod lib/utils/database.ts uses a Dexie-managed
 * IndexedDB ('maic-classroom-storage'). BrowserDocumentStore opens its own
 * raw IDBDatabase named 'dongniai-maic-documents'. The two coexist — no
 * interference, no migration, no risk to existing data.
 *
 * Why a singleton: the store caches its dbPromise internally; instantiating
 * multiple instances is safe but wastes a connection. Lazy-instantiate on
 * first call so server-side rendering never touches IndexedDB.
 */
import { BrowserDocumentStore, type DocumentStore } from '@openmaic/storage';
import type { Stage, Scene } from '@openmaic/dsl';
import { dongniaiSceneValidator } from './dongniai-scene-validator';

const DONGNIAI_DOC_DB_NAME = 'dongniai-maic-documents';

let _instance: DocumentStore<Scene> | null = null;

export function getDongniaiDocumentStore(): DocumentStore<Scene> {
  if (typeof window === 'undefined') {
    throw new Error(
      '[storage-bridge] DocumentStore is browser-only; called on the server',
    );
  }
  if (!_instance) {
    _instance = new BrowserDocumentStore<Scene>({
      dbName: DONGNIAI_DOC_DB_NAME,
      validateScene: dongniaiSceneValidator,
    });
  }
  return _instance;
}

/** Test-only — reset the singleton so a fresh options object can be injected. */
export function __resetDongniaiDocumentStore(): void {
  _instance = null;
}

/**
 * App-widened Stage — the prod Stage (DSL) plus dongniai business fields
 * (outlineId / lessonIndex / progress / currentSceneId — see lib/utils/database.ts
 * StageRecord). DocumentStore stores this whole object as the stage row;
 * unknown extra fields ride along verbatim (StageRow = Stage & {DSL_VERSION_KEY}).
 */
export type DongniaiStage = Stage & {
  outlineId?: number | null;
  lessonIndex?: number | null;
  progress?: number;
  currentSceneId?: string;
};